<script setup lang="ts">
import { Mail as MailIcon } from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { useField, useForm } from 'vee-validate'
import { onMounted, nextTick, ref } from 'vue'

import BaseSpinner from '@/components/ui/BaseSpinner.vue'
import { useAuthStore } from '@/stores/authStore'

interface Props {
  initialEmail?: string
}

interface Emits {
  success: []
  switchToLogin: []
}

const props = withDefaults(defineProps<Props>(), {
  initialEmail: '',
})
const emit = defineEmits<Emits>()

// Auth store
const authStore = useAuthStore()
const { isLoading, error } = storeToRefs(authStore)

// Refs for focus management
const emailInputRef = ref<HTMLInputElement | null>(null)

// Form setup
const { handleSubmit, resetForm } = useForm()

// Email validation function
const validateEmail = (value: string) => {
  if (!value) {
    return 'Email is required'
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(value)) {
    return 'Please enter a valid email'
  }
  return true
}

// Form fields
const {
  value: email,
  errorMessage: emailError,
  meta: emailMeta,
} = useField('email', validateEmail, {
  initialValue: props.initialEmail,
})

// Handle form submission
const onSubmit = handleSubmit(async (values) => {
  authStore.clearError()

  const success = await authStore.requestPasswordReset({
    email: values.email,
  })

  if (success) {
    resetForm()
    emit('success')
  }
})

// Switch to login
const handleSwitchToLogin = () => {
  resetForm()
  authStore.clearError()
  emit('switchToLogin')
}

// Clear errors and focus when component mounts
onMounted(() => {
  authStore.clearError()

  // Focus the first input after component is mounted
  nextTick(() => {
    if (emailInputRef.value) {
      emailInputRef.value.focus()
    }
  })
})
</script>

<template>
  <!-- Add live region for form status announcements -->
  <div id="reset-status" class="sr-only" aria-live="polite" aria-atomic="true">
    <span v-if="isLoading">Sending password reset email, please wait...</span>
    <span v-else-if="error">{{ error }}</span>
  </div>

  <form @submit.prevent="onSubmit" class="space-y-4" novalidate>
    <!-- Instructions -->
    <div class="text-center mb-6">
      <p class="text-sm text-gray-600" id="reset-instructions">
        Enter your email address and we'll send you a link to reset your password.
      </p>
    </div>

    <!-- Email field -->
    <div>
      <label for="reset-email" class="block text-sm font-medium text-gray-700 mb-2">
        Email Address <span class="text-red-500" aria-label="required">*</span>
      </label>
      <input
        id="reset-email"
        ref="emailInputRef"
        v-model="email"
        type="email"
        autocomplete="email"
        required
        class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors"
        :class="{
          'border-red-300 focus:ring-red-500': emailError,
          'border-green-300 focus:ring-green-500': emailMeta.valid && emailMeta.touched,
          'border-gray-300 focus:ring-primary-500':
            !emailError && (!emailMeta.valid || !emailMeta.touched),
        }"
        placeholder="Enter your email address"
        :disabled="isLoading"
        :aria-invalid="emailError ? 'true' : 'false'"
        :aria-describedby="emailError ? 'reset-email-error' : 'reset-email-help'"
      />
      <div v-if="!emailError" id="reset-email-help" class="sr-only">
        Enter the email address associated with your account
      </div>
      <div v-if="emailError" id="reset-email-error" class="mt-1 text-sm text-red-600" role="alert">
        {{ emailError }}
      </div>
    </div>

    <!-- Error message -->
    <div
      v-if="error"
      class="p-3 bg-red-50 border border-red-200 rounded-md"
      role="alert"
      aria-live="polite"
    >
      <p class="text-sm text-red-600">{{ error }}</p>
    </div>

    <!-- Submit button -->
    <button
      type="submit"
      :disabled="isLoading"
      :aria-describedby="isLoading ? 'reset-loading-status' : undefined"
      :class="[
        // Layout & sizing
        'w-full',
        'flex',
        'items-center',
        'justify-center',
        'px-4',
        'py-2',
        // Colors & states
        'bg-primary-600',
        'hover:bg-primary-700',
        'disabled:bg-primary-400',
        'text-white',
        // Typography & effects
        'font-medium',
        'rounded-md',
        'transition-colors',
        // Focus & interaction
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-primary-500',
        'focus:ring-offset-2',
        'hover:cursor-pointer',
      ]"
    >
      <MailIcon v-if="!isLoading" class="w-4 h-4 mr-2" aria-hidden="true" />
      <BaseSpinner v-else class="mr-2" aria-hidden="true" />
      {{ isLoading ? 'Sending Reset Link...' : 'Send Reset Link' }}
    </button>
    <div v-if="isLoading" id="reset-loading-status" class="sr-only">
      Please wait while we send you a password reset link
    </div>

    <!-- Footer link -->
    <div class="text-center">
      <div class="flex items-center justify-center space-x-1 text-sm">
        <span class="text-gray-600">Remember your password?</span>
        <button
          type="button"
          @click="handleSwitchToLogin"
          :class="[
            // Colors & interaction
            'text-primary-600',
            'hover:text-primary-800',
            'font-medium',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-sm',
          ]"
          :disabled="isLoading"
          aria-describedby="login-help"
        >
          Log in
        </button>
      </div>
      <div id="login-help" class="sr-only">Click to return to the log-in form</div>
    </div>
  </form>
</template>
